"use client";

import { useEffect } from "react";

/**
 * "ResizeObserver loop limit exceeded" (and the Firefox/Safari variant "…loop
 * completed with undelivered notifications") is a benign browser-internal warning,
 * not an app bug — it fires whenever a ResizeObserver callback's own work changes
 * the size of what it's observing (exactly what the Leaflet map preview's resize
 * handling does). It's dispatched as a genuine uncaught `window` error outside
 * React's render cycle, so no error boundary can catch it, and Next's client
 * runtime otherwise treats it as fatal. Suppressing this one well-known, narrowly
 * matched pattern (not errors generally) is the standard fix recommended across
 * the ecosystem for this exact symptom.
 */
const BENIGN_PATTERN = /ResizeObserver loop/i;

function isBenign(message: string | null | undefined): boolean {
  return !!message && BENIGN_PATTERN.test(message);
}

export function GlobalErrorGuard() {
  useEffect(() => {
    function onError(event: ErrorEvent) {
      if (isBenign(event.message) || isBenign(event.error?.message)) {
        event.stopImmediatePropagation();
        event.preventDefault();
      }
    }
    function onRejection(event: PromiseRejectionEvent) {
      const message = event.reason instanceof Error ? event.reason.message : String(event.reason ?? "");
      if (isBenign(message)) {
        event.stopImmediatePropagation();
        event.preventDefault();
      }
    }

    window.addEventListener("error", onError, true);
    window.addEventListener("unhandledrejection", onRejection, true);
    return () => {
      window.removeEventListener("error", onError, true);
      window.removeEventListener("unhandledrejection", onRejection, true);
    };
  }, []);

  return null;
}
